<script setup lang="ts">
import { FEATURE_PAGES } from '~/constants/featurePages'

/**
 * Grid of links to the feature subpages in the template's service-grid style:
 * heading on the left, one button on the right, flat white cards on the grey
 * band that lift on hover with a sliding arrow.
 *
 * Used at the bottom of each subpage as cross-links (with the current page
 * left out); `compact` gives the smaller heading and tighter spacing for that.
 */
const props = defineProps<{
  /** Path of the page rendering this grid, so it does not link to itself. */
  exclude?: string
  /** Smaller heading and tighter spacing for the in-page version. */
  compact?: boolean
}>()

const { t } = useI18n()

const links = computed(() => FEATURE_PAGES.filter(page => page.path !== props.exclude))

// On the pricing page itself the button would point at the page you are on.
const showPricing = computed(() => props.exclude !== '/pricing')
</script>

<template>
  <section class="sofax surface-alt">
    <div :class="compact ? 'section-space-sm' : 'section-space'">
      <div class="shell">
        <div class="mb-10 flex flex-wrap items-center justify-between gap-8 lg:mb-14">
          <div class="max-w-3xl">
            <h2 :class="{ '!text-3xl md:!text-4xl': compact }">
              {{ compact ? t('marketing.featureLinks.compactTitle') : t('marketing.featureLinks.title') }}
            </h2>
            <p v-if="!compact" class="mt-5 text-lg">{{ t('marketing.featureLinks.subtitle') }}</p>
          </div>
          <SofaxBtn
            v-if="showPricing"
            to="/pricing"
            :label="t('marketing.featureLinks.pricingCta')"
            variant="dark"
            :class="{ 'btn-sm': compact }"
          />
        </div>

        <ul class="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          <li v-for="page in links" :key="page.id">
            <LocaleLink
              :to="page.path"
              class="group flex h-full flex-col rounded-[0.625rem] bg-white p-[1.875rem] transition-all duration-300 hover:shadow-lift dark:bg-gray-800"
            >
              <!-- Template illustration on a light tile; the line art is dark -->
              <span
                class="inline-flex shrink-0 items-center justify-center rounded-[0.625rem] bg-surface"
                :class="compact ? 'size-16' : 'size-20'"
              >
                <img
                  :src="page.illustration"
                  alt=""
                  aria-hidden="true"
                  width="80"
                  height="80"
                  :class="compact ? 'size-10' : 'size-12'"
                  loading="lazy"
                >
              </span>

              <div class="display-heading display-heading-4 mb-3 mt-6">
                {{ t(page.titleKey) }}
              </div>

              <p class="mb-6 flex-1 text-base">{{ t(page.descriptionKey) }}</p>

              <SofaxArrowLink as="span" :label="t('marketing.featureLinks.more')" />
            </LocaleLink>
          </li>
        </ul>
      </div>
    </div>
  </section>
</template>
